import { TCompanyCollection, TOverviewPeriod } from "@/types";

/**
 * Every TanStack Query key, by domain. A mutation invalidates a root
 * (`taskKeys.all`) and every key derived from it falls with it; the
 * predicates that match on `queryKey[0]` compare against these roots.
 *
 * Shapes are the ones that used to be inline: a key's identity is what
 * the cache is addressed by, so changing one is a cache migration, not
 * a rename.
 */
export const meKeys = {
	all: ["me"] as const,
	settings: ["me", "settings"] as const,
	passkeys: ["me", "passkeys"] as const,
	sessions: ["me", "sessions"] as const,
};

/** A workspace's identity (`GET /workspaces/{id}`); its members live under membersKeys. */
export const workspaceKeys = {
	detail: (id: number) => ["workspace", id] as const,
};

export const viewKeys = {
	all: ["views"] as const,
	detail: (id: number) => ["views", id] as const,
	summary: (period: unknown) => ["views", "summary", period] as const,
	/** The edit modal's own copy of the view, apart from the list's. */
	edit: (id?: number) => ["view", "edit", id] as const,
};

export const projectKeys = {
	all: ["projects"] as const,
	ofView: (viewId: number) => ["projects", viewId] as const,
	summary: (viewId: number, period: TOverviewPeriod) =>
		["projects", viewId, "summary", period] as const,
	/** A client's own projects, apart from any view's. */
	ofClientSummary: (clientId: number, period: TOverviewPeriod) =>
		["projects", "client", clientId, "summary", period] as const,
	/** The workspace's projects with their figures (the /projects page). */
	listSummary: (period: TOverviewPeriod) =>
		["projects", "summary", period] as const,
	detail: (id: number) => ["projects", "detail", id] as const,
};

export const taskKeys = {
	all: ["tasks"] as const,
	detail: (id: number | "new" | undefined) => ["tasks", id] as const,
	list: (
		viewId: number | undefined,
		projectId: number | undefined,
		filters: unknown
	) => ["tasks", viewId ?? null, projectId ?? null, filters] as const,
	/** The home view's month: every task of the period, unpaged. */
	ofPeriod: (
		viewId: number | undefined,
		projectId: number | undefined,
		dateFrom: unknown,
		dateTo: unknown
	) => ["tasks", "forView", viewId, projectId, dateFrom, dateTo] as const,
};

export const analyticsKeys = {
	projectOverview: (id: number, period: TOverviewPeriod) =>
		["analytics", "overview", "project", id, period] as const,
	viewOverview: (id: number, period: TOverviewPeriod) =>
		["analytics", "overview", "view", id, period] as const,
};

/** Companies and clients: one entity, two collections (see TCompanyKind). */
export const companyKeys = {
	collection: (collection: TCompanyCollection) => [collection] as const,
	detail: (collection: TCompanyCollection, id: number) =>
		[collection, id] as const,
	billing: (companyId: number | undefined) =>
		["company-billing", companyId] as const,
};

export const invoiceKeys = {
	all: ["invoices"] as const,
	list: (filters: unknown) => ["invoices", filters] as const,
	ofReport: (reportId: number) => ["invoices", { reportId }] as const,
	summary: ["invoices-summary"] as const,
	detail: (id: number | undefined) => ["invoice", id] as const,
	prefill: (reportId: number | undefined) =>
		["invoice-prefill", reportId] as const,
	nextNumber: (companyId: number | undefined) =>
		["invoice-next-number", companyId] as const,
};

export const reportKeys = {
	saved: ["saved-reports"] as const,
	savedDetail: (id: number | undefined) => ["saved-report", id] as const,
	/** The report form's task listing, keyed on what it was asked for. */
	tasks: (
		scope: "project" | "client",
		id: number | undefined,
		dateFrom: unknown,
		dateTo: unknown
	) => ["reports", scope, id, dateFrom, dateTo] as const,
};

export const subscriptionKeys = {
	current: ["subscription"] as const,
	/** Another workspace's, for the pages that manage one (`current` stays the user's own). */
	of: (workspaceId: number) => ["subscription", workspaceId] as const,
	plans: ["plans"] as const,
};

export const adminKeys = {
	all: ["admin"] as const,
	users: (q: string, page: number) => ["admin", "users", q, page] as const,
	workspace: (id: number) => ["admin", "workspace", id] as const,
	plans: ["admin", "plans"] as const,
	stripeEvents: (limit: number) => ["admin", "stripe-events", limit] as const,
	metrics: ["admin", "metrics"] as const,
	jobs: ["admin", "jobs"] as const,
	features: ["admin", "features"] as const,
};

export const ssoKeys = {
	callback: ["ssoCallback"] as const,
};

/**
 * Every query whose answer counts tasks: the listings themselves, the
 * figures (stats, overviews), the projects' and views' summaries (hours,
 * task count, last task), a report's task listing. A task created, edited
 * or deleted makes them all stale - one rule, so no call site forgets one.
 */
export const isTaskDependentQuery = (queryKey: readonly unknown[]): boolean => {
	const [root] = queryKey;
	return (
		root === "tasks" ||
		root === "analytics" ||
		root === "reports" ||
		((root === "projects" || root === "views") &&
			queryKey.includes("summary"))
	);
};
